import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useNoGroupingGoalListTree } from '../../hooks/use-no-grouping-goal-list-tree';
import { GroupingGoalListProps } from '../../types';
import { LifeAreaHeader } from '../life-area-header';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './no-grouping-goal-list.style';

const droppedTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const NoGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  isMultiSelectModifierPressed,
  onUpdateProgress,
  hideLifeArea,
  hideArchived,
  hideCompleted,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const treeItems = useNoGroupingGoalListTree(goals, {
    hideArchived,
    hideCompleted,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];
    const updatedSections: { id: ID; goalSorting: ID[] }[] = [];

    // Collect section updates
    updatedItems.forEach((item) => {
      if (item.type === TaskListItemTypes.Goal) {
        if (!goalsMap[item.id]) {
          return;
        }
        const parentItem =
          updatedItems.find(({ children }) => children?.includes(item.id)) ??
          Object.values(treeItems).find(({ children }) =>
            children?.includes(item.id),
          );

        const parentGoal = parentItem ? goalsMap[parentItem.id] : undefined;

        if (!parentGoal) {
          updatedDocs.push({
            id: item.id,
            goalSorting: item.children ?? [],
            parentIds: null,
          });
          return;
        }
        updatedDocs.push({
          id: item.id,
          parentIds: [...(parentGoal.parentIds ?? []), parentGoal.id],
          lifeAreaId: null,
        });
        return;
      }

      if (
        [rootId, ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID].includes(item.id)
      ) {
        updatedSections.push({ id: item.id, goalSorting: item.children ?? [] });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const { close, position, payload } = useContextMenu();

  const renderItem = useCallback(
    ({ itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goals.find((g) => g.id === itemId);
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea={hideLifeArea}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (itemData.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;

        if (itemId === COMPLETED_SECTION_ID) {
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.completed.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
        if (itemId === ARCHIVED_SECTION_ID) {
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.archived.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
      }

      return null;
    },
    [
      treeItems,
      goals,
      onUpdateProgress,
      hideLifeArea,
      t,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={_onReorder}
        dropTypes={droppedTypes}
        dragTypes={dragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
